import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import type { SopCitation } from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';
import { formatClock } from '../format.ts';

interface Props {
  citation: SopCitation | null;
  videoUrl?: string;
  onClose: () => void;
}

const CitationPanel: React.FC<Props> = ({ citation, videoUrl, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v && citation) {
      const seek = () => {
        v.currentTime = citation.start_seconds;
      };
      if (v.readyState >= 1) seek();
      else v.addEventListener('loadedmetadata', seek, { once: true });
    }
  }, [citation]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!citation) return null;
  return (
    <aside
      className="fixed right-0 top-0 z-40 flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-gray-200 bg-white p-5 shadow-xl"
      aria-label="Citation source"
    >
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Source {citation.marker.slice(1)}</h3>
          <p className="text-xs text-gray-500">
            Video {formatClock(citation.start_seconds)} – {formatClock(citation.end_seconds)}
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close source" className="rounded p-1 text-gray-500 hover:bg-gray-100">
          <X className="h-5 w-5" />
        </button>
      </div>

      <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">What was said</h4>
      <blockquote className="mb-4 rounded border-l-4 border-blue-200 bg-blue-50 p-3 text-sm text-gray-800">
        {citation.quote || 'No transcript text for this range.'}
      </blockquote>

      {citation.frame_url && (
        <>
          <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">On screen</h4>
          <img src={citation.frame_url} alt="Screenshot from the video" className="mb-4 w-full rounded border border-gray-200" />
        </>
      )}

      {videoUrl && (
        <>
          <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Play from here</h4>
          <video ref={videoRef} className="w-full rounded bg-black" controls preload="metadata" src={videoUrl} />
        </>
      )}
      <p className="mt-4 text-xs text-gray-400">
        Text comes from an automatic transcript, so names and figures may be misheard. Check them against the video.
      </p>
    </aside>
  );
};

export default CitationPanel;
